import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Header from "../../../components/header/header";
import SettingsSubmenu from "../components/submenu/settings_submenu";
import SettingsSection from "../components/section/section";
import styles from "../styles/settings.module.css";
import axiosInstance from "../../../AxiosInstance";
import sectionstyles from "../components/section/settings_section.module.css";
import Modal from "../../../components/modal/modal";
import {TextInput} from "../../../components/input/inputs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faFileDownload, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";
import {formatSuspensionReason} from "../../../utils/suspensionReason";

export default function PaymentSettings() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [billingData, setBillingData] = useState(null);
    const [showBillingModal, setShowBillingModal] = useState(false);
    const [billingForm, setBillingForm] = useState({});
    const [saveError, setSaveError] = useState(null);
    const [saving, setSaving] = useState(false);

    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [reactivating, setReactivating] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [actionSuccess, setActionSuccess] = useState(null);

    // Country, state and city selectors (cascading)
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    useEffect(() => {
        let isMounted = true;

        axiosInstance.get('billing/overview/').then((response) => {
            if (!isMounted) return;
            setBillingData(response.data);
            if (response.data.billing_info) {
                setBillingForm(response.data.billing_info);
            }
            setLoading(false);
        }).catch((err) => {
            if (!isMounted) return;
            console.error('Error fetching billing overview:', err);
            setLoading(false);
        });

        axiosInstance.get('countries/').then((response) => {
            if (!isMounted) return;
            setCountries(response.data.countries || []);
        }).catch((err) => {
            console.error('Error fetching countries:', err);
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const fetchStates = (countryCode) => {
        if (!countryCode) {
            setStates([]);
            return;
        }
        setLoadingStates(true);
        axiosInstance.get(`countries/?country_code=${countryCode}`).then((response) => {
            setStates(response.data.states || []);
            setLoadingStates(false);
        }).catch((err) => {
            console.error('Error fetching states:', err);
            setStates([]);
            setLoadingStates(false);
        });
    };

    const fetchCities = (countryCode, state) => {
        if (!countryCode || !state) {
            setCities([]);
            return;
        }
        setLoadingCities(true);
        axiosInstance.get(`countries/?country_code=${countryCode}&state=${encodeURIComponent(state)}`).then((response) => {
            setCities(response.data.cities || []);
            setLoadingCities(false);
        }).catch((err) => {
            console.error('Error fetching cities:', err);
            setCities([]);
            setLoadingCities(false);
        });
    };

    // Load states when country changes
    useEffect(() => {
        if (billingForm.country) {
            fetchStates(billingForm.country);
        } else {
            setStates([]);
            setCities([]);
        }
    }, [billingForm.country]);

    // Load cities when state changes
    useEffect(() => {
        if (billingForm.country && billingForm.state) {
            fetchCities(billingForm.country, billingForm.state);
        } else {
            setCities([]);
        }
    }, [billingForm.state]);

    const fetchBillingOverview = () => {
        setLoading(true);
        axiosInstance.get('billing/overview/').then((response) => {
            setBillingData(response.data);
            if (response.data.billing_info) {
                setBillingForm(response.data.billing_info);
            }
            setLoading(false);
        }).catch((err) => {
            console.error('Error fetching billing overview:', err);
            setLoading(false);
        });
    };

    const openBillingModal = () => {
        if (billingData?.billing_info) {
            setBillingForm({...billingData.billing_info});
        }
        setSaveError(null);
        setShowBillingModal(true);
    };

    const saveBillingInfo = () => {
        setSaving(true);
        setSaveError(null);

        axiosInstance.put('billing/info/', {
            tax_id: billingForm.tax_id || '',
            business_name: billingForm.business_name || '',
            address: billingForm.address || '',
            city: billingForm.city || '',
            state: billingForm.state || '',
            postal_code: billingForm.postal_code || '',
            country: billingForm.country || '',
            billing_email: billingForm.billing_email || '',
            billing_phone: billingForm.billing_phone || '',
            invoice_due_days: billingForm.invoice_due_days || 30
        }).then((response) => {
            setSaving(false);
            setShowBillingModal(false);
            fetchBillingOverview();
        }).catch((err) => {
            setSaving(false);
            if (err.response?.data?.message) {
                setSaveError(err.response.data.message);
            } else {
                setSaveError(t('settings.payments.errorSaving'));
            }
        });
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'active': { label: t('settings.payments.statusActive'), color: '#10B981', bg: '#D1FAE5' },
            'pending_payment': { label: t('settings.payments.statusPendingPayment'), color: '#F59E0B', bg: '#FEF3C7' },
            'cancelled': { label: t('settings.payments.statusCancelled'), color: '#64748B', bg: '#F1F5F9' },
            'past_due': { label: t('settings.payments.statusPastDue'), color: '#EF4444', bg: '#FEE2E2' },
            'suspended': { label: t('settings.payments.statusSuspended'), color: '#DC2626', bg: '#FEE2E2' },
            'trialing': { label: t('settings.payments.statusTrial'), color: '#3B82F6', bg: '#DBEAFE' }
        };
        const statusInfo = statusMap[status] || { label: status, color: '#64748B', bg: '#F1F5F9' };
        return (
            <span style={{
                backgroundColor: statusInfo.bg,
                color: statusInfo.color,
                padding: '4px 12px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: '600'
            }}>
                {statusInfo.label}
            </span>
        );
    };

    const handleCancelSubscription = async () => {
        setCancelling(true);
        setActionError(null);

        try {
            const response = await axiosInstance.post('subscription_request/cancel/');
            setActionSuccess(t('settings.payments.cancelledSuccess', {date: formatDate(response.data.access_until)}));
            setShowCancelModal(false);
            fetchBillingOverview();
        } catch (err) {
            console.error('Error cancelling subscription:', err);
            setActionError(err.response?.data?.error || t('settings.payments.errorCancelling'));
        } finally {
            setCancelling(false);
        }
    };

    const handleReactivateSubscription = async () => {
        setReactivating(true);
        setActionError(null);

        try {
            await axiosInstance.post('subscription_request/reactivate/');
            setActionSuccess(t('settings.payments.reactivatedSuccess'));
            fetchBillingOverview();
        } catch (err) {
            console.error('Error reactivating subscription:', err);
            setActionError(err.response?.data?.error || t('settings.payments.errorReactivating'));
        } finally {
            setReactivating(false);
        }
    };

    const downloadInvoice = async (invoiceId, invoiceNumber) => {
        try {
            const response = await axiosInstance.get(`billing/invoices/${invoiceId}/download/`, {
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `invoice_${invoiceNumber || invoiceId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading invoice:', err);
            // Fallback: open in new tab
            window.open(`${axiosInstance.defaults.baseURL}billing/invoices/${invoiceId}/download/`, '_blank');
        }
    };

    const getInvoiceStatusBadge = (invoice) => {
        if (invoice.paid) {
            return (
                <span style={{
                    backgroundColor: '#D1FAE5',
                    color: '#10B981',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>{t('settings.payments.invoicePaid')}</span>
            );
        } else if (invoice.expired) {
            return (
                <span style={{
                    backgroundColor: '#FEE2E2',
                    color: '#EF4444',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>{t('settings.payments.invoiceExpired')}</span>
            );
        } else {
            return (
                <span style={{
                    backgroundColor: '#FEF3C7',
                    color: '#F59E0B',
                    padding: '4px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '500'
                }}>{t('settings.payments.invoicePending')}</span>
            );
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '$0.00';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    const getCountryName = (countryCode) => {
        if (!countryCode) return '-';
        const country = countries.find(c => c.code === countryCode);
        return country ? country.name : countryCode;
    };

    if (loading) {
        return (
            <div className={styles.dashboard_container}>
                <Header sidebarIndex={5}/>
                <div className={styles.dashboardInnerContainer}>
                    <SettingsSubmenu active={3}/>
                    <div className={'d-flex justify-content-center align-items-center'} style={{height: '300px'}}>
                        <div>{t('common.loading')}</div>
                    </div>
                </div>
            </div>
        );
    }

    const subscription = billingData?.subscription;
    const billingInfo = billingData?.billing_info;
    const invoices = billingData?.invoices || [];

    return (
        <div className={styles.dashboard_container}>
            <Header sidebarIndex={5}/>

            {/* Cancel Subscription Modal */}
            <Modal
                show={showCancelModal}
                setter={setShowCancelModal}
                name={t('settings.payments.cancelSubscription')}
                buttonName={cancelling ? t('settings.payments.cancelling') : t('settings.payments.yesCancelSubscription')}
                onClick={!cancelling ? handleCancelSubscription : undefined}
            >
                <div style={{textAlign: 'center', padding: '10px 0'}}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: '#FEE2E2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px',
                        fontSize: '28px',
                        color: '#EF4444'
                    }}>!</div>
                    <div style={{fontSize: '16px', color: '#1E293B', marginBottom: '12px'}}>
                        {t('settings.payments.cancelModalQuestion')}
                    </div>
                    <div style={{fontSize: '14px', color: '#64748B', lineHeight: '1.6'}}>
                        <p style={{marginBottom: '8px'}}>
                            {t('settings.payments.cancelModalP1')}
                        </p>
                        <p style={{marginBottom: '8px'}}>
                            {t('settings.payments.cancelModalP2')}
                        </p>
                        <p style={{color: '#10B981', fontWeight: '500'}}>
                            {t('settings.payments.cancelModalP3')}
                        </p>
                    </div>
                </div>
            </Modal>

            <Modal
                show={showBillingModal}
                setter={setShowBillingModal}
                name={t('settings.payments.billingInfo')}
                buttonName={saving ? t('common.saving') : t('common.save')}
                onClick={!saving ? saveBillingInfo : undefined}
            >
                {saveError && (
                    <div style={{
                        backgroundColor: '#FEE2E2',
                        color: '#DC2626',
                        padding: '10px',
                        borderRadius: '6px',
                        marginBottom: '15px',
                        fontSize: '14px'
                    }}>
                        {saveError}
                    </div>
                )}
                <TextInput name={t('settings.payments.taxId')} internalName={'tax_id'} form={billingForm} setForm={setBillingForm} placeholder={'20123456789'}/>
                <TextInput name={t('settings.payments.businessName')} internalName={'business_name'} form={billingForm} setForm={setBillingForm} placeholder={'My Company Inc.'}/>
                <TextInput name={t('settings.payments.address')} internalName={'address'} form={billingForm} setForm={setBillingForm} placeholder={'123 Main Street'}/>

                {/* Country */}
                <div className={'form-group mb-3'}>
                    <div style={{fontSize: '13px', fontWeight: '500', color: '#344054', marginBottom: '4px'}}>{t('settings.payments.country')}</div>
                    <select
                        className={'form-control rounded-2'}
                        style={{
                            border: '1px solid #D0D5DD',
                            padding: '10px 12px',
                            fontSize: '14px',
                            color: '#344054'
                        }}
                        value={billingForm.country || ''}
                        onChange={(e) => {
                            setBillingForm({
                                ...billingForm,
                                country: e.target.value,
                                state: '',
                                city: ''
                            });
                        }}
                    >
                        <option value="">{t('settings.payments.selectCountry')}</option>
                        {countries.map((country) => (
                            <option key={country.code} value={country.code}>{country.name}</option>
                        ))}
                    </select>
                </div>

                {/* State/Region */}
                <div className={'form-group mb-3'}>
                    <div style={{fontSize: '13px', fontWeight: '500', color: '#344054', marginBottom: '4px'}}>{t('settings.payments.stateRegion')}</div>
                    {states.length > 0 ? (
                        <select
                            className={'form-control rounded-2'}
                            style={{
                                border: '1px solid #D0D5DD',
                                padding: '10px 12px',
                                fontSize: '14px',
                                color: '#344054'
                            }}
                            value={billingForm.state || ''}
                            onChange={(e) => setBillingForm({...billingForm, state: e.target.value, city: ''})}
                            disabled={loadingStates || !billingForm.country}
                        >
                            <option value="">{loadingStates ? t('common.loading') : t('settings.payments.selectState')}</option>
                            {states.map((state, index) => (
                                <option key={index} value={state}>{state}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className={'form-control rounded-2'}
                            style={{
                                border: '1px solid #D0D5DD',
                                padding: '10px 12px',
                                fontSize: '14px',
                                color: '#344054'
                            }}
                            placeholder={billingForm.country ? t('settings.payments.enterState') : t('settings.payments.selectCountryFirst')}
                            value={billingForm.state || ''}
                            onChange={(e) => setBillingForm({...billingForm, state: e.target.value})}
                            disabled={!billingForm.country}
                        />
                    )}
                </div>

                {/* City */}
                <div className={'form-group mb-3'}>
                    <div style={{fontSize: '13px', fontWeight: '500', color: '#344054', marginBottom: '4px'}}>{t('settings.payments.city')}</div>
                    {cities.length > 0 ? (
                        <select
                            className={'form-control rounded-2'}
                            style={{
                                border: '1px solid #D0D5DD',
                                padding: '10px 12px',
                                fontSize: '14px',
                                color: '#344054'
                            }}
                            value={billingForm.city || ''}
                            onChange={(e) => setBillingForm({...billingForm, city: e.target.value})}
                            disabled={loadingCities || !billingForm.state}
                        >
                            <option value="">{loadingCities ? t('common.loading') : t('settings.payments.selectCity')}</option>
                            {cities.map((city, index) => (
                                <option key={index} value={city}>{city}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type="text"
                            className={'form-control rounded-2'}
                            style={{
                                border: '1px solid #D0D5DD',
                                padding: '10px 12px',
                                fontSize: '14px',
                                color: '#344054'
                            }}
                            placeholder={billingForm.state ? t('settings.payments.enterCity') : t('settings.payments.selectStateFirst')}
                            value={billingForm.city || ''}
                            onChange={(e) => setBillingForm({...billingForm, city: e.target.value})}
                            disabled={!billingForm.state}
                        />
                    )}
                </div>

                {/* Postal Code */}
                <TextInput name={t('settings.payments.postalCode')} internalName={'postal_code'} form={billingForm} setForm={setBillingForm} placeholder={'15023'}/>
                <TextInput name={t('settings.payments.billingEmail')} internalName={'billing_email'} form={billingForm} setForm={setBillingForm} placeholder={t('settings.payments.billingEmailPlaceholder')} email={true}/>
                <TextInput name={t('settings.payments.billingPhone')} internalName={'billing_phone'} form={billingForm} setForm={setBillingForm} placeholder={t('settings.payments.billingPhonePlaceholder')}/>
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <SettingsSubmenu active={3}/>

                {/* Current Plan Section */}
                <div className={`${sectionstyles.container} mt-2`}>
                    <div className={'d-flex justify-content-between flex-wrap align-items-start'} style={{marginBottom: '16px'}}>
                        <div style={{textAlign: "left"}}>
                            <div className={`${sectionstyles.title} mb-1`}>{t('settings.payments.currentPlan')}</div>
                            <div className={sectionstyles.desc}>{t('settings.payments.currentPlanDesc')}</div>
                        </div>
                        <div className={styles.primaryButton} onClick={() => navigate('/pricing')} style={{cursor: 'pointer', marginTop: '8px'}}>
                            {subscription ? t('settings.payments.changePlan') : t('settings.payments.viewPlans')}
                        </div>
                    </div>

                    {/* Success/Error Messages */}
                    {actionSuccess && (
                        <div style={{
                            backgroundColor: '#D1FAE5',
                            color: '#065F46',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '16px',
                            fontSize: '14px'
                        }}>
                            {actionSuccess}
                            <button
                                onClick={() => setActionSuccess(null)}
                                style={{float: 'right', background: 'none', border: 'none', color: '#065F46', cursor: 'pointer'}}
                            >×</button>
                        </div>
                    )}
                    {actionError && (
                        <div style={{
                            backgroundColor: '#FEE2E2',
                            color: '#DC2626',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            marginBottom: '16px',
                            fontSize: '14px'
                        }}>
                            {actionError}
                            <button
                                onClick={() => setActionError(null)}
                                style={{float: 'right', background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer'}}
                            >×</button>
                        </div>
                    )}

                    {subscription ? (
                        <div className={`${styles.squareContainer} p-4 mb-3`}>
                            <div className={'d-flex justify-content-between align-items-start flex-wrap'}>
                                <div>
                                    <div style={{fontSize: '24px', fontWeight: '600', color: '#1E293B'}}>
                                        {subscription.payment_plan?.name || 'Plan'}
                                    </div>
                                    <div style={{color: '#64748B', marginTop: '4px'}}>
                                        {subscription.payment_plan?.description}
                                    </div>
                                    {subscription.billing_cycle && (
                                        <div style={{
                                            display: 'inline-block',
                                            background: '#EEF2FF',
                                            color: '#4F46E5',
                                            padding: '4px 10px',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            marginTop: '8px'
                                        }}>
                                            {subscription.billing_cycle === 'yearly' ? t('settings.payments.annualBilling') : t('settings.payments.monthlyBilling')}
                                        </div>
                                    )}
                                </div>
                                <div className={'text-end'}>
                                    <div style={{fontSize: '32px', fontWeight: '700', color: '#4F46E5'}}>
                                        {formatCurrency(subscription.payment_plan?.price)}
                                        <span style={{fontSize: '14px', fontWeight: '400', color: '#64748B'}}>{t('settings.payments.perMonth')}</span>
                                    </div>
                                    <div className={'mt-2'}>{getStatusBadge(subscription.status)}</div>
                                </div>
                            </div>

                            {/* Warning: Subscription marked for cancellation */}
                            {subscription.cancel_at_period_end && subscription.status !== 'cancelled' && (
                                <div style={{
                                    backgroundColor: '#FEF3C7',
                                    border: '1px solid #FCD34D',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginTop: '16px'
                                }}>
                                    <div style={{color: '#92400E', fontWeight: '600', marginBottom: '8px'}}>
                                        {t('settings.payments.subscriptionWillNotRenew')}
                                    </div>
                                    <div style={{color: '#92400E', fontSize: '14px', marginBottom: '12px'}}>
                                        {t('settings.payments.accessUntil')} <strong>{formatDate(subscription.current_period_end)}</strong>
                                    </div>
                                    <button
                                        onClick={handleReactivateSubscription}
                                        disabled={reactivating}
                                        style={{
                                            background: '#4F46E5',
                                            color: 'white',
                                            border: 'none',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: reactivating ? 'not-allowed' : 'pointer',
                                            opacity: reactivating ? 0.6 : 1
                                        }}
                                    >
                                        {reactivating ? t('settings.payments.reactivating') : t('settings.payments.reactivate')}
                                    </button>
                                </div>
                            )}

                            {/* Warning: Subscription suspended */}
                            {subscription.status === 'suspended' && (
                                <div style={{
                                    backgroundColor: '#FEE2E2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginTop: '16px'
                                }}>
                                    <div style={{color: '#DC2626', fontWeight: '600', marginBottom: '8px'}}>
                                        {t('settings.payments.accountSuspended')}
                                    </div>
                                    <div style={{color: '#DC2626', fontSize: '14px', marginBottom: '8px'}}>
                                        {formatSuspensionReason(subscription.suspension_reason, t, 'settings.payments') || t('settings.payments.suspendedMsg')}
                                    </div>
                                    <div style={{color: '#991B1B', fontSize: '13px'}}>
                                        {t('settings.payments.contactSupport', {email: 'support@myindustrialdata.com'})}
                                    </div>
                                </div>
                            )}

                            <div style={{
                                display: 'flex',
                                gap: '12px',
                                alignItems: 'flex-start',
                                backgroundColor: '#EFF6FF',
                                border: '1px solid #BFDBFE',
                                borderRadius: '8px',
                                padding: '12px 16px',
                                marginTop: '16px',
                                fontSize: '13px',
                                lineHeight: 1.45
                            }}>
                                <FontAwesomeIcon icon={faInfoCircle} style={{flexShrink: 0, marginTop: '2px', color: '#2563EB'}}/>
                                <div>
                                    <strong style={{display: 'block', marginBottom: '4px', color: '#1E3A8A'}}>
                                        {t('settings.payments.policyTitle')}
                                    </strong>
                                    <span style={{color: '#1F2937'}}>
                                        {t('settings.payments.policyBody')}
                                    </span>
                                </div>
                            </div>

                            <hr style={{borderColor: '#E2E8F0', margin: '20px 0'}}/>

                            <div className={'d-flex flex-wrap'} style={{gap: '30px'}}>
                                <div>
                                    <div style={{color: '#64748B', fontSize: '12px', textTransform: 'uppercase'}}>{t('settings.payments.currentPeriod')}</div>
                                    <div style={{fontWeight: '500'}}>{formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}</div>
                                </div>
                                <div>
                                    <div style={{color: '#64748B', fontSize: '12px', textTransform: 'uppercase'}}>{t('settings.payments.maxDevices')}</div>
                                    <div style={{fontWeight: '500'}}>{subscription.max_devices || '-'} max.</div>
                                </div>
                                <div>
                                    <div style={{color: '#64748B', fontSize: '12px', textTransform: 'uppercase'}}>{t('settings.payments.maxUsers')}</div>
                                    <div style={{fontWeight: '500'}}>{subscription.max_users || '-'} max.</div>
                                </div>
                                <div>
                                    <div style={{color: '#64748B', fontSize: '12px', textTransform: 'uppercase'}}>{t('settings.payments.maxCompanies')}</div>
                                    <div style={{fontWeight: '500'}}>{subscription.max_companies || '-'} max.</div>
                                </div>
                            </div>

                            {/* Cancel Subscription Button */}
                            {subscription.status === 'active' && !subscription.cancel_at_period_end && (
                                <div style={{marginTop: '24px', paddingTop: '20px', borderTop: '1px solid #E2E8F0'}}>
                                    <button
                                        onClick={() => setShowCancelModal(true)}
                                        style={{
                                            background: 'transparent',
                                            color: '#EF4444',
                                            border: '1px solid #EF4444',
                                            padding: '10px 20px',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {t('settings.payments.cancelSubscription')}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className={`${styles.squareContainer} p-4 mb-3`} style={{textAlign: 'center', color: '#64748B'}}>
                            {t('settings.payments.noActiveSubscription')}
                        </div>
                    )}
                </div>

                {/* Billing Information Section */}
                <SettingsSection title={t('settings.payments.billingInfo')} description={t('settings.payments.billingInfoDesc')}>
                    <div side={'left'}>
                        <div className={styles.lightButton} onClick={openBillingModal}>{t('common.edit')}</div>
                    </div>

                    <div side={'right'} className={'d-flex'}>
                        <div className={`${styles.squareContainer} p-3`}>
                            {billingInfo ? (
                                <div className={`d-flex flex-wrap ${styles.infoLines}`}>
                                    <div style={{flexBasis: "30%"}}>{t('settings.payments.taxId')}</div>
                                    <div style={{flexBasis: "70%"}}>{billingInfo.tax_id || '-'}</div>

                                    <div style={{flexBasis: "30%"}}>{t('settings.payments.businessName')}</div>
                                    <div style={{flexBasis: "70%"}}>{billingInfo.business_name || '-'}</div>

                                    <div style={{flexBasis: "30%"}}>{t('settings.payments.address')}</div>
                                    <div style={{flexBasis: "70%"}}>
                                        {billingInfo.address ? (
                                            <>
                                                {billingInfo.address}
                                                {billingInfo.city && `, ${billingInfo.city}`}
                                                {billingInfo.state && `, ${billingInfo.state}`}
                                                {billingInfo.postal_code && ` ${billingInfo.postal_code}`}
                                                {billingInfo.country && `, ${getCountryName(billingInfo.country)}`}
                                            </>
                                        ) : '-'}
                                    </div>

                                    <div style={{flexBasis: "30%"}}>{t('settings.payments.email')}</div>
                                    <div style={{flexBasis: "70%"}}>{billingInfo.billing_email || '-'}</div>

                                    <div style={{flexBasis: "30%"}}>{t('settings.payments.phone')}</div>
                                    <div style={{flexBasis: "70%"}}>{billingInfo.billing_phone || '-'}</div>
                                </div>
                            ) : (
                                <div style={{textAlign: 'center', color: '#64748B', padding: '20px'}}>
                                    {t('settings.payments.noBillingInfo')}
                                </div>
                            )}
                        </div>
                    </div>
                </SettingsSection>

                {/* Invoice History Section */}
                <SettingsSection title={t('settings.payments.invoiceHistory')} description={t('settings.payments.invoiceHistoryDesc')}>
                    <div side={'left'}>
                        <div style={{color: '#64748B', fontSize: '14px'}}>
                            {t('settings.payments.invoicesFound', {count: invoices.length})}
                        </div>
                    </div>

                    <div side={'right'} className={'d-flex'}>
                        <div className={`${styles.squareContainer}`} style={{overflow: 'auto'}}>
                            {invoices.length > 0 ? (<>
                                {/* Desktop table */}
                                <table style={{width: '100%', borderCollapse: 'collapse', minWidth: '700px', display: 'table'}} className={'d-none d-md-table'}>
                                    <thead>
                                        <tr style={{backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}>
                                            <th style={{padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase'}}>{t('settings.payments.invoiceNumber')}</th>
                                            <th style={{padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase'}}>{t('settings.payments.date')}</th>
                                            <th style={{padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase'}}>{t('settings.payments.amount')}</th>
                                            <th style={{padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase'}}>{t('common.status')}</th>
                                            <th style={{padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase'}}>{t('settings.payments.maxDevices')}</th>
                                            <th style={{padding: '12px 16px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#64748B', textTransform: 'uppercase'}}>{t('common.actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {invoices.map((invoice, index) => (
                                            <tr key={invoice.id || index} style={{borderBottom: index < invoices.length - 1 ? '1px solid #E2E8F0' : 'none'}}>
                                                <td style={{padding: '14px 16px', fontSize: '14px', fontWeight: '500', color: '#1E293B'}}>{invoice.invoice_number || `#${invoice.id}`}</td>
                                                <td style={{padding: '14px 16px', fontSize: '14px'}}>{invoice.month?.toString().padStart(2, '0')}/{invoice.year}</td>
                                                <td style={{padding: '14px 16px', fontSize: '14px', fontWeight: '600'}}>
                                                    {formatCurrency(invoice.amount)}
                                                    {invoice.amount_with_tax && (
                                                        <div style={{fontSize: '11px', color: '#64748B', fontWeight: '400'}}>
                                                            {t('settings.payments.incTax', {amount: formatCurrency(invoice.amount_with_tax)})}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{padding: '14px 16px'}}>{getInvoiceStatusBadge(invoice)}</td>
                                                <td style={{padding: '14px 16px', textAlign: 'center', fontSize: '14px'}}>{invoice.device_count || 0}</td>
                                                <td style={{padding: '14px 16px', textAlign: 'center'}}>
                                                    <button onClick={() => downloadInvoice(invoice.id, invoice.invoice_number)}
                                                        style={{background: '#EEF2FF', border: 'none', borderRadius: '6px', padding: '8px 12px', cursor: 'pointer', color: '#4F46E5', fontSize: '13px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '6px'}}
                                                        onMouseOver={(e) => e.currentTarget.style.background = '#E0E7FF'}
                                                        onMouseOut={(e) => e.currentTarget.style.background = '#EEF2FF'}>
                                                        <FontAwesomeIcon icon={faFileDownload}/> PDF
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Mobile cards */}
                                <div className={'d-md-none'} style={{display: 'flex', flexDirection: 'column', gap: '0'}}>
                                    {invoices.map((invoice, index) => (
                                        <div key={invoice.id || index} style={{
                                            padding: '14px 16px',
                                            borderBottom: index < invoices.length - 1 ? '1px solid #F1F5F9' : 'none',
                                        }}>
                                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px'}}>
                                                <span style={{fontWeight: '600', fontSize: '14px', color: '#1E293B'}}>
                                                    {invoice.invoice_number || `#${invoice.id}`}
                                                </span>
                                                {getInvoiceStatusBadge(invoice)}
                                            </div>
                                            <div style={{fontSize: '12px', color: '#64748B', display: 'flex', flexDirection: 'column', gap: '3px'}}>
                                                <span><b style={{color: '#334155'}}>{t('settings.payments.date')}:</b> {invoice.month?.toString().padStart(2, '0')}/{invoice.year}</span>
                                                <span><b style={{color: '#334155'}}>{t('settings.payments.amount')}:</b> {formatCurrency(invoice.amount)}
                                                    {invoice.amount_with_tax && <span style={{color: '#94A3B8'}}> ({t('settings.payments.incTax', {amount: formatCurrency(invoice.amount_with_tax)})})</span>}
                                                </span>
                                                <span><b style={{color: '#334155'}}>{t('settings.payments.maxDevices')}:</b> {invoice.device_count || 0}</span>
                                            </div>
                                            <button onClick={() => downloadInvoice(invoice.id, invoice.invoice_number)}
                                                style={{marginTop: '10px', background: '#EEF2FF', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', color: '#4F46E5', fontSize: '13px', fontWeight: '500', display: 'inline-flex', alignItems: 'center', gap: '6px'}}>
                                                <FontAwesomeIcon icon={faFileDownload}/> PDF
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </>) : (
                                <div style={{textAlign: 'center', color: '#64748B', padding: '40px'}}>
                                    {t('settings.payments.noInvoices')}
                                </div>
                            )}
                        </div>
                    </div>
                </SettingsSection>

            </div>
        </div>
    );
}
